import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Loader2, Mail, Send, X } from 'lucide-react';
import { IconButton, LoadError } from '../ui';
import { Select, TextField } from '../forms';
import { c } from '../../styles/theme';
import type { AdminSupportTicket, AdminSupportTicketThread } from '../../types/domain';
import { focusableWithin, wrapTabFocus } from '../../lib/focusTrap';

const STATUS_OPTIONS = ['Open', 'In progress', 'Resolved'] as const;
const SELECT_STYLE = { padding: '8px 10px', fontSize: 12, minWidth: 140 } as const;
const REPLY_STYLE = { padding: '8px 10px', fontSize: 13 } as const;
const LEVEL_BADGE_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 999,
  padding: '2px 8px',
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.02em',
  background: c.accentSoft,
  border: `1px solid ${c.accentBorder}`,
  color: c.accent,
} as const;

export interface AdminSupportDetailProps {
  ticket: AdminSupportTicket | null;
  thread: AdminSupportTicketThread | null;
  threadError?: string | null;
  pending: boolean;
  loading: boolean;
  onBack: () => void;
  onRetry?: () => void;
  onChangeStatus: (status: AdminSupportTicketThread['status']) => void;
  onSendReply: (message: string, closeTicket?: boolean) => void;
}

function MessageBubble({
  author,
  channel,
  body,
  createdAt,
  admin,
}: {
  author: string;
  channel: string;
  body: string;
  createdAt?: string;
  admin: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: admin ? 'flex-end' : 'flex-start',
        gap: 6,
      }}
    >
      <div
        style={{
          maxWidth: '100%',
          background: admin ? c.accentSoft : c.surface2,
          border: `1px solid ${admin ? c.accentBorder : c.border}`,
          borderRadius: 12,
          padding: '10px 12px',
        }}
      >
        <p style={{ fontSize: 11, color: c.text3, margin: '0 0 6px' }}>
          {author} · {channel}
        </p>
        <p style={{ fontSize: 12, color: c.text1, margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{body}</p>
      </div>
      <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>
        {createdAt ? new Date(createdAt).toLocaleString() : '—'}
      </p>
    </div>
  );
}

export default function AdminSupportDetail({
  ticket,
  thread,
  threadError,
  pending,
  loading,
  onBack,
  onRetry,
  onChangeStatus,
  onSendReply,
}: AdminSupportDetailProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const onBackRef = useRef(onBack);
  const headingId = useId();
  const [reply, setReply] = useState('');
  const focusScopeId = ticket?.id ?? (loading ? 'loading' : null);

  onBackRef.current = onBack;

  useEffect(() => {
    if (!focusScopeId) return;
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = rootRef.current;
    if (root) focusableWithin(root)[0]?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      const current = rootRef.current;
      if (!current) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onBackRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      wrapTabFocus(current, e);
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      const target = previouslyFocused.current;
      previouslyFocused.current = null;
      if (target?.isConnected) target.focus();
    };
  }, [focusScopeId]);

  useEffect(() => {
    setReply('');
  }, [thread?.id, ticket?.id]);

  const messages = useMemo(() => thread?.messages ?? [], [thread?.messages]);

  if (!ticket && !loading) return null;

  return (
    <>
      <div className="lyx-drawer-overlay" onClick={onBack} aria-hidden="true" />
      <div
        ref={rootRef}
        className="lyx-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        style={{ background: c.surface1, borderLeft: `1px solid ${c.border}` }}
      >
        <div style={{ padding: '18px 20px 32px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 14,
            }}
          >
            <p
              id={headingId}
              style={{
                fontFamily: 'Space Grotesk',
                fontWeight: 600,
                fontSize: 15,
                margin: 0,
              }}
            >
              Ticket Details
              <span className="lyx-visually-hidden"> — {ticket?.subject ?? 'Loading'}</span>
            </p>
            <IconButton label="Close" onClick={onBack}>
              <X size={18} color={c.text3} />
            </IconButton>
          </div>

          {loading && !ticket ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: c.text2, fontSize: 12 }}>
              <Loader2 size={16} className="lyx-spin" />
              Loading ticket thread…
            </div>
          ) : ticket ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <h2
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontWeight: 600,
                      fontSize: 18,
                      margin: '0 0 6px',
                      color: c.text1,
                    }}
                  >
                    {ticket.subject}
                  </h2>
                  <p style={{ fontSize: 12, color: c.text3, margin: 0 }}>
                    {ticket.category} · {ticket.date}
                  </p>
                  <p style={{ fontSize: 11, color: c.text3, margin: '6px 0 0' }}>
                    Ticket ID: {ticket.id}
                  </p>
                </div>
                <Select
                  ariaLabel={`Ticket status for ${ticket.subject}`}
                  value={ticket.status}
                  onChange={(value: string) => onChangeStatus(value as AdminSupportTicketThread['status'])}
                  options={STATUS_OPTIONS}
                  disabled={pending}
                  style={SELECT_STYLE}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 4px', color: c.text2 }}>Requester</p>
                <p style={{ fontSize: 12, color: c.text1, margin: '0 0 2px' }}>{ticket.requesterName}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <p style={{ fontSize: 12, color: c.text3, margin: 0 }}>{ticket.requesterEmail}</p>
                  <span style={LEVEL_BADGE_STYLE}>{`level ${ticket.requesterPurchaseLevel}`}</span>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 8px', color: c.text2 }}>Conversation</p>
                {loading && !thread ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: c.text2, fontSize: 12 }}>
                    <Loader2 size={16} className="lyx-spin" />
                    Loading ticket thread…
                  </div>
                ) : threadError ? (
                  <LoadError message={threadError} onRetry={onRetry}>
                    <p style={{ fontSize: 11, color: c.text3, margin: '8px 0 0' }}>Ticket ID: {ticket.id}</p>
                  </LoadError>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {messages.length > 0 ? (
                      messages.map((message) => (
                        <MessageBubble
                          key={message.id}
                          author={message.authorName}
                          channel={message.channel === 'email' ? 'Email' : 'Web'}
                          body={message.body}
                          createdAt={message.createdAt}
                          admin={message.authorType === 'admin'}
                        />
                      ))
                    ) : (
                      <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>No thread messages yet.</p>
                    )}
                  </div>
                )}
              </div>

              {thread ? (
                <div
                  style={{
                    background: c.surface2,
                    border: `1px solid ${c.border}`,
                    borderRadius: 12,
                    padding: '14px 14px 12px',
                    marginBottom: 14,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <Mail size={14} color={c.text2} />
                    <p style={{ fontSize: 12, fontWeight: 600, color: c.text2, margin: 0 }}>Reply by email</p>
                  </div>
                  <TextField
                    label="Reply message"
                    value={reply}
                    onChange={setReply}
                    multiline
                    rows={5}
                    placeholder="Write the message that should be emailed to the requester"
                    marginBottom={12}
                    style={REPLY_STYLE}
                  />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const trimmed = reply.trim();
                        if (!trimmed) return;
                        onSendReply(trimmed, false);
                        setReply('');
                      }}
                      disabled={pending || !reply.trim()}
                      style={{
                        border: `1px solid ${c.accentBorder}`,
                        background: c.accentSoft,
                        color: c.text1,
                        borderRadius: 10,
                        padding: '9px 12px',
                        fontSize: 12,
                        cursor: pending || !reply.trim() ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <Send size={13} />
                      Send reply
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const trimmed = reply.trim();
                        if (!trimmed) return;
                        onSendReply(trimmed, true);
                        setReply('');
                      }}
                      disabled={pending || !reply.trim()}
                      style={{
                        border: `1px solid ${c.border}`,
                        background: c.surface1,
                        color: c.text1,
                        borderRadius: 10,
                        padding: '9px 12px',
                        fontSize: 12,
                        cursor: pending || !reply.trim() ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Send & close ticket
                    </button>
                  </div>
                </div>
              ) : null}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>
                  Created: {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : '—'}
                </p>
                <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>
                  Updated: {ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleString() : '—'}
                </p>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
